/**
 * Network Analysis Tab Configuration
 *
 * Centralizes tab-specific filter behavior following SOLID principles:
 * - Single Responsibility: Each tab config defines its own filter requirements
 * - Open/Closed: Easy to add new tabs without modifying filter logic
 * - Dependency Inversion: Filters depend on configuration, not hardcoded logic
 */

export enum NetworkAnalysisTabId {
    Summary = 0,
    Results = 1,
    Explore = 2,
    DisruptionAnalysis = 3
}

/**
 * Filter component types used across Network Analysis tabs
 */
export enum FilterComponentType {
    /** NetworkAnalysisGeographyFilter - used by Summary, Results, Explore tabs */
    NetworkAnalysisGeography = 'NETWORK_ANALYSIS_GEOGRAPHY',
    /** DisruptionAnalysisFilter - used by Disruption Analysis tab */
    DisruptionAnalysis = 'DISRUPTION_ANALYSIS'
}

/**
 * Multi-select configuration for a filter
 */
export interface MultiSelectConfig {
    enabled: boolean;
    maxSelections?: number;
    showPrimaryIndicator?: boolean;
    sortSelectedFirst?: boolean;
    hideTags?: boolean;
}

/**
 * Configuration for a single filter on a tab
 */
export interface FilterConfig {
    visible: boolean;
    multiSelect?: MultiSelectConfig;
}

/**
 * Network Analysis Geography filter settings
 * Used by Summary, Results, and Explore tabs via NetworkAnalysisGeographyFilter component
 */
export interface NetworkAnalysisGeographyFilters {
    distanceCalcType: FilterConfig;
    adequacyStandard: FilterConfig;
    standardVersion: FilterConfig;
    patientRoster: FilterConfig;
    state: FilterConfig;
    county: FilterConfig;
    specialty: FilterConfig;
}

/**
 * Disruption Analysis filter settings
 * Used by Disruption Analysis tab via DisruptionAnalysisFilter component
 */
export interface DisruptionAnalysisFilters {
    lineOfBusiness: FilterConfig;
    patientRoster: FilterConfig;
    state: FilterConfig;
    county: FilterConfig;
    zip: FilterConfig;
    specialty: FilterConfig;
}

/**
 * Base configuration common to all tabs
 */
interface BaseTabConfig {
    id: NetworkAnalysisTabId;
    label: string;
    requiresStateAndCounty?: boolean;
}

/**
 * Configuration for tabs using Network Analysis Geography filters
 */
export interface NetworkAnalysisGeographyTabConfig extends BaseTabConfig {
    filterType: FilterComponentType.NetworkAnalysisGeography;
    filters: NetworkAnalysisGeographyFilters;
}

/**
 * Configuration for tabs using Disruption Analysis filters
 */
export interface DisruptionAnalysisTabConfig extends BaseTabConfig {
    filterType: FilterComponentType.DisruptionAnalysis;
    filters: DisruptionAnalysisFilters;
}

/**
 * Complete configuration for any Network Analysis tab (union type)
 */
export type NetworkAnalysisTabConfig = NetworkAnalysisGeographyTabConfig | DisruptionAnalysisTabConfig;

/**
 * Tab configurations defining filter behavior per tab
 */
export const NETWORK_ANALYSIS_TAB_CONFIGS: Record<NetworkAnalysisTabId, NetworkAnalysisTabConfig> = {
    [NetworkAnalysisTabId.Summary]: {
        id: NetworkAnalysisTabId.Summary,
        label: 'Summary',
        filterType: FilterComponentType.NetworkAnalysisGeography,
        filters: {
            distanceCalcType: {
                visible: true,
                multiSelect: undefined
            },
            adequacyStandard: {
                visible: true,
                multiSelect: undefined
            },
            standardVersion: {
                visible: true,
                multiSelect: undefined
            },
            patientRoster: {
                visible: true,
                multiSelect: undefined
            },
            state: {
                visible: true,
                multiSelect: undefined
            },
            county: {
                visible: true,
                multiSelect: undefined
            },
            specialty: {
                visible: true,
                multiSelect: {
                    enabled: true,
                    maxSelections: 9,
                    showPrimaryIndicator: false,
                    sortSelectedFirst: true,
                    hideTags: false
                }
            }
        },
        requiresStateAndCounty: false
    },
    [NetworkAnalysisTabId.Results]: {
        id: NetworkAnalysisTabId.Results,
        label: 'Results',
        filterType: FilterComponentType.NetworkAnalysisGeography,
        filters: {
            distanceCalcType: {
                visible: true,
                multiSelect: undefined
            },
            adequacyStandard: {
                visible: true,
                multiSelect: undefined
            },
            standardVersion: {
                visible: true,
                multiSelect: undefined
            },
            patientRoster: {
                visible: true,
                multiSelect: undefined
            },
            state: {
                visible: true,
                multiSelect: undefined
            },
            county: {
                visible: true,
                multiSelect: {
                    enabled: true,
                    maxSelections: 10,
                    showPrimaryIndicator: true,
                    sortSelectedFirst: true,
                    hideTags: false
                }
            },
            specialty: {
                visible: true,
                multiSelect: {
                    enabled: true,
                    maxSelections: 9,
                    showPrimaryIndicator: true,
                    sortSelectedFirst: true,
                    hideTags: false
                }
            }
        },
        requiresStateAndCounty: true
    },
    [NetworkAnalysisTabId.Explore]: {
        id: NetworkAnalysisTabId.Explore,
        label: 'Explore Additional Providers',
        filterType: FilterComponentType.NetworkAnalysisGeography,
        filters: {
            distanceCalcType: {
                visible: true,
                multiSelect: undefined
            },
            adequacyStandard: {
                visible: true,
                multiSelect: undefined
            },
            standardVersion: {
                visible: true,
                multiSelect: undefined
            },
            patientRoster: {
                visible: true,
                multiSelect: undefined
            },
            state: {
                visible: true,
                multiSelect: undefined
            },
            county: {
                visible: true,
                multiSelect: {
                    enabled: true,
                    maxSelections: 10,
                    showPrimaryIndicator: true,
                    sortSelectedFirst: true,
                    hideTags: false
                }
            },
            specialty: {
                visible: true,
                multiSelect: undefined
            }
        },
        requiresStateAndCounty: true
    },
    [NetworkAnalysisTabId.DisruptionAnalysis]: {
        id: NetworkAnalysisTabId.DisruptionAnalysis,
        label: 'Disruption Analysis',
        filterType: FilterComponentType.DisruptionAnalysis,
        filters: {
            lineOfBusiness: {
                visible: true,
                multiSelect: undefined
            },
            patientRoster: {
                visible: true,
                multiSelect: undefined
            },
            state: {
                visible: true,
                multiSelect: undefined
            },
            county: {
                visible: true,
                multiSelect: undefined
            },
            zip: {
                visible: true,
                multiSelect: undefined
            },
            specialty: {
                visible: true,
                multiSelect: undefined
            }
        },
        requiresStateAndCounty: false
    }
};

/**
 * Get the complete tab configuration
 */
export function getTabConfig(tabId: NetworkAnalysisTabId): NetworkAnalysisTabConfig {
    return NETWORK_ANALYSIS_TAB_CONFIGS[tabId];
}

/**
 * Get filter type (component) for a specific tab
 */
export function getTabFilterType(tabId: NetworkAnalysisTabId): FilterComponentType {
    return NETWORK_ANALYSIS_TAB_CONFIGS[tabId].filterType;
}

/**
 * Get filter configuration for a specific tab
 */
export function getTabFilterConfig(tabId: NetworkAnalysisTabId): NetworkAnalysisGeographyFilters | DisruptionAnalysisFilters {
    return NETWORK_ANALYSIS_TAB_CONFIGS[tabId].filters;
}

/**
 * Get NetworkAnalysisGeography filter configuration (for Summary, Results, Explore tabs)
 * Returns undefined if the tab uses a different filter type
 */
export function getNetworkAnalysisGeographyFilters(tabId: NetworkAnalysisTabId): NetworkAnalysisGeographyFilters | undefined {
    const config = NETWORK_ANALYSIS_TAB_CONFIGS[tabId];
    if (config.filterType === FilterComponentType.NetworkAnalysisGeography) {
        return config.filters;
    }
    return undefined;
}

/**
 * Check if a filter supports multi-select on a specific tab (for NetworkAnalysisGeography filters only)
 * Returns false for tabs that don't use NetworkAnalysisGeography filters or when filter doesn't support multi-select
 */
export function isMultiSelectEnabled(
    tabId: NetworkAnalysisTabId,
    filterName: keyof NetworkAnalysisGeographyFilters
): boolean {
    const filters = getNetworkAnalysisGeographyFilters(tabId);
    if (!filters) {
        return false;
    }
    return filters[filterName].multiSelect?.enabled ?? false;
}

/**
 * Get the primary value from a pipe-separated filter value
 * Used when switching from multi-select to single-select tabs
 */
export function getPrimaryFilterValue(filterValue: string): string {
    if (!filterValue) return '';
    const values = filterValue.split('|');
    return values[0] || '';
}

/**
 * Normalize a filter value when switching tabs
 * Returns primary value if switching to single-select, otherwise returns original value
 *
 * @param currentValue - Current filter value (possibly pipe-separated)
 * @param isMultiSelectEnabled - Whether multi-select is enabled on the target tab
 * @returns Normalized filter value
 */
export function normalizeFilterValue(currentValue: string, isMultiSelectEnabled: boolean): string {
    if (!currentValue) return '';

    // If multi-select is enabled, keep all selections
    if (isMultiSelectEnabled) return currentValue;

    // If switching to single-select and multiple values exist, keep only primary
    const values = currentValue.split('|');
    return values.length > 1 ? values[0] : currentValue;
}

/**
 * Helper to normalize and apply a filter value if it changed
 * @returns The normalized value
 */
export function normalizeAndApply(
    currentValue: string,
    tabId: NetworkAnalysisTabId,
    filterName: keyof NetworkAnalysisGeographyFilters,
    applyFn: (value: string) => void
): string {
    const normalized = normalizeFilterValue(currentValue, isMultiSelectEnabled(tabId, filterName));
    if (normalized !== currentValue) {
        applyFn(normalized);
    }
    return normalized;
}
